import { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

const DeepLinkFallback = () => {
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [hasAttempted, setHasAttempted] = useState(false);
  const [appNotInstalled, setAppNotInstalled] = useState(false);

  const IOS_APP_STORE = 'https://apps.apple.com/app/travelmaker-ai-audio-guide/id6742571605';
  const ANDROID_PLAY_STORE = 'https://play.google.com/store/apps/details?id=com.comrades.travelmaker';

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const storeUrl = isIOS ? IOS_APP_STORE : ANDROID_PLAY_STORE;

  const searchParams = new URLSearchParams(location.search);
  const title = searchParams.get('title');

  const generateDeepLink = useCallback(() => {
    // Always use custom URL scheme to avoid Universal Link loop
    // Universal Links should only be the entry point (handled by AASA),
    // not the redirect target from this fallback page
    const deepLinkBase = 'travelmaker://';
    return deepLinkBase + location.pathname + location.search;
  }, [location]);

  const handleOpenApp = useCallback(() => {
    if (isLoading || hasAttempted) return;

    setIsLoading(true);
    setHasAttempted(true);
    
    const deepLink = generateDeepLink();
    window.location.href = deepLink;
    const startTime = Date.now();

    const checkAppOpened = () => {
      const timeElapsed = Date.now() - startTime;
      
      if (document.hidden) {
        // App opened successfully
        setIsLoading(false);
        return;
      }
      
      if (timeElapsed > 2500) {
        // App didn't open
        setIsLoading(false);
        setAppNotInstalled(true);
      } else {
        setTimeout(checkAppOpened, 100);
      }
    };
    
    setTimeout(checkAppOpened, 500);
  }, [isLoading, hasAttempted, generateDeepLink]);

  useEffect(() => {
    // Auto-attempt to open app on page load (only once)
    if (!hasAttempted) {
      const timer = setTimeout(() => {
        handleOpenApp();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [hasAttempted, handleOpenApp]);

  const getMessage = () => {
    if (appNotInstalled) {
      return 'TravelMaker app is not installed. Download it to continue.';
    }
    if (title) {
      return `Check out "${decodeURIComponent(title)}" on TravelMaker`;
    }
    return 'Open this content in the TravelMaker app';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-600 to-purple-700 flex items-center justify-center p-5">
      <div className="bg-white rounded-3xl p-10 max-w-md w-full shadow-2xl text-center">
        <div className="w-24 h-24 mx-auto mb-5 rounded-2xl overflow-hidden">
          <img 
            src="/apple-touch-icon.png" 
            alt="TravelMaker" 
            className="w-full h-full object-cover"
          />
        </div>
        
        <h1 className="text-3xl font-bold text-gray-800 mb-3">TravelMaker</h1>
        <p className="text-gray-600 mb-8 leading-relaxed">{getMessage()}</p>
        
        <div className="flex flex-col gap-4">
          <button
            onClick={handleOpenApp}
            disabled={isLoading}
            className={`font-semibold py-4 px-8 rounded-xl transition-all hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-70 flex items-center justify-center ${
              appNotInstalled 
                ? 'bg-gray-100 text-gray-800 hover:bg-gray-200' 
                : 'bg-teal-500 text-white hover:bg-teal-600'
            }`}
          >
            {isLoading && (
              <span className="inline-block w-5 h-5 border-3 border-white/30 border-t-white rounded-full animate-spin mr-2"></span>
            )}
            <span>
              {isLoading ? 'Opening...' : appNotInstalled ? 'Try Again' : 'Open in App'}
            </span>
          </button>
          
          <a
            href={storeUrl}
            className={`font-semibold py-4 px-8 rounded-xl transition-all hover:-translate-y-0.5 hover:shadow-lg ${
              appNotInstalled
                ? 'bg-teal-500 text-white hover:bg-teal-600'
                : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
            }`}
          >
            Download App
          </a>
        </div>
      </div>
    </div>
  );
};

export default DeepLinkFallback;
